import { useCookie } from "#app";
import { context, propagation } from "@opentelemetry/api";
import axios, {
	AxiosError,
	isAxiosError,
	type Axios,
	type AxiosRequestConfig,
	type AxiosResponse,
} from "axios";
import { useDBConnectionError } from "~/composables/useDBConnectionError";
import { createSessionAuthError, useSessionManager } from "~/composables/useSessionManager";
import { R3Promise } from "../../../../../r3utils/src";
import { R3FetchDataBaseImpl } from "../R3Fetching.interface";

type R3AxiosRequestConfig<T> = Omit<AxiosRequestConfig, "params"> & {
	params?: T;
};

export const allowedRoutesWithoutCookies = [
	"/api/usuarios/empresasdousuario",
	"/api/login",
	"/api/utils/ping",
	"/api/utils/data",
];

const COLLECTOR_DEVICE_ID_KEY = "harpia:collector-device-id";

/**
 * Returns anonymous, stable headers used only for collector capacity metrics.
 * Desktop stations are intentionally excluded from the collector population.
 */
function getCollectorTelemetryHeaders(): Record<string, string> {
	if (typeof window === "undefined" || typeof navigator === "undefined") return {};

	const capacitorWindow = window as typeof window & {
		Capacitor?: { isNativePlatform?: () => boolean, getPlatform?: () => string }
	};
	const isNative = capacitorWindow.Capacitor?.isNativePlatform?.() === true;
	const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|Mobile/i.test(
		navigator.userAgent,
	);
	if (!isNative && !isMobile) return {};

	let deviceId = window.localStorage.getItem(COLLECTOR_DEVICE_ID_KEY);
	if (!deviceId) {
		deviceId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
			? crypto.randomUUID()
			: `web-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
		window.localStorage.setItem(COLLECTOR_DEVICE_ID_KEY, deviceId);
	}

	const deviceName = window.localStorage.getItem("deviceName")?.replace(/^"|"$/g, "") || "";
	return {
		"x-harpia-client-type": "collector",
		"x-harpia-device-id": deviceId,
		"x-harpia-device-platform":
			capacitorWindow.Capacitor?.getPlatform?.() || (isNative ? "native" : "mobile-web"),
		...(deviceName ? { "x-harpia-device-name": deviceName } : {}),
	};
}

export class R3FetchDataAxiosImpl extends R3FetchDataBaseImpl {
	private _axios: Axios;
	private _sessionManager = useSessionManager();

	constructor(baseUrl: string) {
		super(baseUrl);
		this._axios = axios.create({ baseURL: this.baseUrl });

		this._axios.interceptors.request.use((config) => {
			const currentContext = context.active();
			const carrier = {
				...config.headers,
				...getCollectorTelemetryHeaders(),
			} as Record<string, string>;
			propagation.inject(currentContext, carrier);
			config.headers = carrier;
			return config;
		});

		// Garantir que qualquer 503 de conexão dispare o modal também
		this._axios.interceptors.response.use(
			(response) => {
				return response;
			},
			async (error) => {
				if (error.response?.status === 503) {
					const db = useDBConnectionError();
					db.show();
				}
				if (
					error.response?.status === 401
					&& !this.isRouteAllowedWithoutCookies(error.config?.url)
				) {
					await this._sessionManager.handleAuthFailure("unauthorized", undefined, 401);
				}
				return Promise.reject(this.normalizeError(error));
			}
		);
	}

	private isRouteAllowedWithoutCookies(endpoint?: string): boolean {
		const route = endpoint?.split("?")[0];
		return route !== undefined && allowedRoutesWithoutCookies.includes(route);
	}

	private getAuthHeaders(authCookie?: string | null, userCookie?: string | null) {
		return {
			Authorization: authCookie ? `Bearer ${authCookie}` : "",
			user: userCookie ?? "",
		};
	}

	private async ensureAuthenticated(endpoint: string) {
		let authCookie = null;
		if (typeof document !== 'undefined') {
			const match = document.cookie.match(new RegExp('(^| )accessToken=([^;]+)'));
			if (match) authCookie = decodeURIComponent(match[2]);
		}

		if (!authCookie && !this.isRouteAllowedWithoutCookies(endpoint)) {
			await this._sessionManager.handleAuthFailure("missing-token", undefined, 401);
		}
		return authCookie;
	}

	private normalizeError(error: unknown) {
		if (!isAxiosError(error)) {
			return error;
		}

		if (
			error.response?.status === 401
			&& !this.isRouteAllowedWithoutCookies(error.config?.url)
		) {
			return createSessionAuthError("unauthorized", error.message, 401);
		}

		const responseMessage = typeof error.response?.data === "object"
			? (error.response?.data as { response?: string; MENSAGEM?: string })?.response
				?? (error.response?.data as { response?: string; MENSAGEM?: string })?.MENSAGEM
			: undefined;

		const normalizedError = new AxiosError(
			responseMessage ?? error.message,
			error.code,
			error.config,
			error.request,
			error.response,
		);

		return Object.assign(normalizedError, {
			status: error.response?.status,
			response: error.response,
		});
	}

	/**
	 * @inheritdoc
	 */
	override async get<Success = unknown, Params = unknown>(
		endpoint: Routes,
		config?: R3AxiosRequestConfig<Params>,
		isAsync?: boolean,
	): Promise<Success> {
		const executor = async () => {
			const authCookie = await this.ensureAuthenticated(endpoint);
			let userCookie = null;
			if (typeof document !== 'undefined') {
				const match = document.cookie.match(new RegExp('(^| )user=([^;]+)'));
				if (match) userCookie = decodeURIComponent(match[2]);
			}

			try {
				const result = await this._axios.get<Success>(endpoint, {
					...config,
					headers: {
						...config?.headers,
						...this.getAuthHeaders(authCookie, userCookie),
					},
				});
				return result.data;
			} catch (error) {
				throw this.normalizeError(error);
			}
		};

		if (!isAsync) {
			return executor();
		}

		return new R3Promise<Success, Error>(async (resolve, reject) => {
			try {
				resolve(await executor());
			} catch (error) {
				reject(this.normalizeError(error) as Error);
			}
		});
	}

	/**
	 * @inheritdoc
	 */
	override async post<Success = unknown, R = unknown>(
		endpoint: Routes,
		data: R,
		isAsync?: boolean,
	): Promise<Success> {
		const executor = async () => {
			const authCookie = await this.ensureAuthenticated(endpoint);
			let userCookie = null;
			if (typeof document !== 'undefined') {
				const match = document.cookie.match(new RegExp('(^| )user=([^;]+)'));
				if (match) userCookie = decodeURIComponent(match[2]);
			}

			try {
				const response = await this._axios.post<Success, AxiosResponse<Success>, R>(endpoint, data, {
					headers: this.getAuthHeaders(authCookie, userCookie),
				});

				return response.data;
			} catch (error) {
				throw this.normalizeError(error);
			}
		};

		if (!isAsync) {
			return executor();
		}

		return new R3Promise<Success, Error>(async (resolve, reject) => {
			try {
				resolve(await executor());
			} catch (error) {
				reject(this.normalizeError(error) as Error);
			}
		});
	}

	/**
	 * @inheritdoc
	 */
	override put<T, R>(endpoint: Routes, params?: R) {
		return new R3Promise<T, Error>(async (resolve, reject) => {
			try {
				const authCookie = await this.ensureAuthenticated(endpoint);
				let userCookie = null;
			if (typeof document !== 'undefined') {
				const match = document.cookie.match(new RegExp('(^| )user=([^;]+)'));
				if (match) userCookie = decodeURIComponent(match[2]);
			}
				const response = await this._axios.put<T>(endpoint, params, {
					headers: this.getAuthHeaders(authCookie, userCookie),
				});
				resolve(response.data);
			} catch (error) {
				reject(this.normalizeError(error) as Error);
			}
		});
	}

	/**
	 * @inheritdoc
	 */
	override patch<R, P = object>(
		endpoint: string,
		params?: P,
		isAsync?: boolean,
	): Promise<R> {
		throw new Error("Method not implemented.");
	}

	override onError<E>() { }
}
